// src/pages/staff/components/StaffAdvancedFilters.tsx

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  DatePicker,
  InputNumber,
  Switch,
  Button,
  Space,
  Typography,
  Tag,
  Card,
} from 'antd';
import {
  FilterOutlined,
  ClearOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { StaffSearchParams } from '../../../types/api.types';
import { STAFF_ROLE_OPTIONS, DEPARTMENT_OPTIONS } from '../../../types/api.types';
import dayjs from 'dayjs';

const {  Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface StaffAdvancedFiltersProps {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: StaffSearchParams) => void;
  initialFilters?: Partial<StaffSearchParams>;
}

interface AdvancedFilters {
  role?: string[];
  department?: string[];
  isActive?: boolean;
  hireDateRange?: [dayjs.Dayjs, dayjs.Dayjs] | null;
  salaryRange?: {
    min?: number;
    max?: number;
  };
  hasPhone?: boolean;
  emailVerified?: boolean;
  hasNotes?: boolean;
  lastLoginRange?: [dayjs.Dayjs, dayjs.Dayjs] | null;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const StaffAdvancedFilters: React.FC<StaffAdvancedFiltersProps> = ({
  open,
  onClose,
  onApplyFilters,
  initialFilters = {},
}) => {
  const [form] = Form.useForm();
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  useEffect(() => {
    if (open) {
      // Préremplir le formulaire avec les filtres existants
      const formValues: AdvancedFilters = {
        role: initialFilters.role ? [initialFilters.role] : undefined,
        department: initialFilters.department ? [initialFilters.department] : undefined,
        isActive: initialFilters.isActive,
        sortBy: initialFilters.sortBy,
        sortOrder: initialFilters.sortOrder,
      };

      form.setFieldsValue(formValues);
      updateActiveFiltersCount(formValues);
    }
  }, [open, initialFilters, form]);

  const updateActiveFiltersCount = (values: AdvancedFilters) => {
    let count = 0;

    if (values.role && values.role.length > 0) count++;
    if (values.department && values.department.length > 0) count++;
    if (values.isActive !== undefined) count++;
    if (values.hireDateRange) count++;
    if (values.salaryRange?.min || values.salaryRange?.max) count++;
    if (values.hasPhone !== undefined) count++;
    if (values.emailVerified !== undefined) count++;
    if (values.hasNotes !== undefined) count++;
    if (values.lastLoginRange) count++;

    setActiveFiltersCount(count);
  };

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    updateActiveFiltersCount(values);
  };

  const handleApplyFilters = async () => {
    try {
      const values = await form.validateFields();

      // Transformer les valeurs du formulaire en paramètres de recherche
      const searchParams: StaffSearchParams = {
        page: 1, // Reset à la première page
        limit: 20,
      };

      if (values.role && values.role.length > 0) {
        searchParams.role = values.role[0]; // L'API ne prend qu'un seul rôle
      }

      if (values.department && values.department.length > 0) {
        searchParams.department = values.department[0]; // L'API ne prend qu'un département
      }

      if (values.isActive !== undefined) {
        searchParams.isActive = values.isActive;
      }

      if (values.sortBy) {
        searchParams.sortBy = values.sortBy;
        searchParams.sortOrder = values.sortOrder || 'desc';
      }

      // Note: Les autres filtres plus complexes nécessiteraient une extension de l'API
      // Pour l'instant, on se contente des filtres supportés par l'API actuelle

      onApplyFilters(searchParams);
      onClose();
    } catch (error) {
      console.error('Erreur de validation des filtres:', error);
    }
  };

  const handleClearFilters = () => {
    form.resetFields();
    setActiveFiltersCount(0);
    onApplyFilters({
      page: 1,
      limit: 20,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    });
    onClose();
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <FilterOutlined />
          <span>Filtres avancés</span>
          {activeFiltersCount > 0 && (
            <Tag color="blue" style={{ borderRadius: '12px' }}>
              {activeFiltersCount} filtre{activeFiltersCount > 1 ? 's' : ''} actif{activeFiltersCount > 1 ? 's' : ''}
            </Tag>
          )}
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      width={700}
      footer={[
        <Button key="clear" icon={<ClearOutlined />} onClick={handleClearFilters}>
          Effacer tout
        </Button>,
        <Button key="cancel" onClick={handleCancel}>
          Annuler
        </Button>,
        <Button
          key="apply"
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleApplyFilters}
        >
          Appliquer les filtres
        </Button>,
      ]}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
        requiredMark={false}
      >
        {/* Filtres de base */}
        <Card size="small" title="Critères de base" style={{ marginBottom: '16px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="role" label="Rôles">
                <Select
                  mode="multiple"
                  placeholder="Sélectionner les rôles"
                  allowClear
                  style={{ width: '100%' }}
                >
                  {STAFF_ROLE_OPTIONS.map(role => (
                    <Option key={role.value} value={role.value}>
                      <Space>
                        <span>{role.label}</span>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {role.description}
                        </Text>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="department" label="Départements">
                <Select
                  mode="multiple"
                  placeholder="Sélectionner les départements"
                  allowClear
                  style={{ width: '100%' }}
                >
                  {DEPARTMENT_OPTIONS.map(dept => (
                    <Option key={dept} value={dept}>
                      {dept}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="isActive" label="Statut d'activité" valuePropName="checked">
                <Switch
                  checkedChildren="Actif"
                  unCheckedChildren="Tous"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Filtres de dates */}
        <Card size="small" title="Critères de dates" style={{ marginBottom: '16px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="hireDateRange" label="Période d'embauche">
                <RangePicker
                  style={{ width: '100%' }}
                  placeholder={['Date début', 'Date fin']}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastLoginRange" label="Dernière connexion">
                <RangePicker
                  style={{ width: '100%' }}
                  placeholder={['Date début', 'Date fin']}
                  format="DD/MM/YYYY"
                  showTime
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Filtres financiers */}
        <Card size="small" title="Critères financiers" style={{ marginBottom: '16px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name={['salaryRange', 'min']} label="Salaire minimum (CFA)">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Montant minimum"
                  formatter={(value) => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : ''}
                  parser={(value: string | undefined): number => value ? parseFloat(value.replace(/\s/g, '')) : 0}
                  min={0}
                  step={10000}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={['salaryRange', 'max']} label="Salaire maximum (CFA)">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Montant maximum"
                  formatter={(value) => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : ''}
                  parser={(value: string | undefined): number => value ? parseFloat(value.replace(/\s/g, '')) : 0}
                  min={0}
                  step={10000}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Filtres additionnels */}
        <Card size="small" title="Critères additionnels" style={{ marginBottom: '16px' }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="hasPhone" label="A un téléphone" valuePropName="checked">
                <Switch size="small" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="emailVerified" label="Email vérifié" valuePropName="checked">
                <Switch size="small" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="hasNotes" label="A des notes" valuePropName="checked">
                <Switch size="small" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Options de tri */}
        <Card size="small" title="Tri des résultats">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sortBy" label="Trier par">
                <Select placeholder="Choisir le critère de tri" allowClear>
                  <Option value="firstName">Prénom</Option>
                  <Option value="lastName">Nom</Option>
                  <Option value="role">Rôle</Option>
                  <Option value="department">Département</Option>
                  <Option value="hireDate">Date d'embauche</Option>
                  <Option value="createdAt">Date de création</Option>
                  <Option value="updatedAt">Date de modification</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sortOrder" label="Ordre">
                <Select placeholder="Ordre de tri" allowClear>
                  <Option value="asc">Croissant (A-Z, 1-9)</Option>
                  <Option value="desc">Décroissant (Z-A, 9-1)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </Modal>
  );
};

export default StaffAdvancedFilters;